import classNames from 'classnames';
import { useParams } from 'next/navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { default as DatePicker_, ReactDatePickerCustomHeaderProps, ReactDatePickerProps } from 'react-datepicker';

import { doTranslation } from '@/app/i18n/client';
import { FormError } from '@/utils/types';

import styles from '@/components/global/forms/date-picker.module.scss';

interface Props extends ReactDatePickerProps {
  dark?: boolean;
  dateFormat: string;
  error?: FormError;
  label?: string;
  mandatory?: boolean;
  name: string;
  onCancel?: () => void;
  onConfirm: (date: Date) => void;
  placeholder?: string;
  reference?: React.RefObject<ReactDatePickerReference>;
  isBookingWidget?: boolean;
  isTripSearch?: boolean;
  hideControls?: boolean;
}

interface ReactDatePickerReference {
  calendar: {
    componentNode: HTMLDivElement;
  };
  input: HTMLInputElement;
}

const DatePicker = (props: Props): React.JSX.Element => {
  const { lng } = useParams() as { lng: string };
  const ref = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(props.selected);
  const [localError, setLocalError] = useState(props.error);

  const { t } = doTranslation(lng);

  const {
    dark,
    dateFormat,
    disabled,
    error,
    id,
    label,
    mandatory,
    name,
    onChange,
    onConfirm,
    placeholder,
    reference,
    isTripSearch,
    ...rest
  } = props;

  const months = useMemo(
    () => [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ],
    []
  );

  const formatWeekDay = (day: string) => (
    <>
      <span className="sr-only">{day}</span>
      <span aria-hidden="true">{day.slice(0, 1)}</span>
    </>
  );

  const setAriaLabel = () => {
    const months_ = document.getElementsByClassName('react-datepicker__month');
    if (months_.length) {
      for (let index = 0; index < months_.length; index++) {
        (months_[index] as HTMLElement).setAttribute(
          'aria-label',
          props.selected ? `${months[props.selected.getMonth()]} ${props.selected.getFullYear()}` : ''
        );
      }
    }
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    decreaseYear,
    increaseMonth,
    increaseYear
  }: ReactDatePickerCustomHeaderProps) => {
    return (
      <div className="react-datepicker__header-container">
        <button
          className="react-datepicker__header-container-previous-year"
          onClick={async () => {
            await decreaseYear();
            modifyScreenReaderAnnouncement();
            setCustomAriaLabelForSelectedDate();
          }}
          type="button"
        >
          <span className="visually-hidden">{t('Go to the previous Year')}</span>
        </button>
        <button
          className="react-datepicker__header-container-previous-month"
          onClick={async () => {
            await decreaseMonth();
            modifyScreenReaderAnnouncement();
            setCustomAriaLabelForSelectedDate();
          }}
          type="button"
        >
          <span className="visually-hidden">{t('Go to the previous month')}</span>
        </button>
        <span>
          {months[date.getMonth()]} {date.getFullYear()}
        </span>
        <button
          className="react-datepicker__header-container-next-month"
          onClick={async () => {
            await increaseMonth();
            modifyScreenReaderAnnouncement();
            setCustomAriaLabelForSelectedDate();
          }}
          type="button"
        >
          <span className="visually-hidden">{t('Go to the next month')}</span>
        </button>
        <button
          className="react-datepicker__header-container-next-year"
          onClick={async () => {
            await increaseYear();
            modifyScreenReaderAnnouncement();
            setCustomAriaLabelForSelectedDate();
          }}
          type="button"
        >
          <span className="visually-hidden">{t('Go to the next year')}</span>
        </button>
      </div>
    );
  };

  useEffect(() => {
    const handleKeydown = (event: any) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        setCustomAriaLabelForSelectedDate();
      }
    };
    if (props.inline) {
      modifyScreenReaderAnnouncement();
      setCustomAriaLabelForSelectedDate();
    }

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown, true);
    };
  });

  const handleKeyDown = (e: any) => {
    if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === 'a' ||
        e.key === 'A' ||
        e.key === 'c' ||
        e.key === 'C' ||
        e.key === 'v' ||
        e.key === 'V' ||
        e.key === 'r' ||
        e.key === 'R' ||
        e.key === 'x' ||
        e.key === 'X')
    ) {
      return;
    }
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete', '/', 'Escape'];
    if (!/^\d+[\/\d]*$/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
      return;
    }
  };

  useEffect(() => {
    setLocalError(error);
  }, [error]);

  const modifyScreenReaderAnnouncement = () => {
    const elements = Array.from(document.querySelectorAll('.react-datepicker__day'));
    if (elements.length) {
      for (const element of elements) {
        if (element.role === 'cell') {
          continue;
        }
        element.removeAttribute('aria-selected');
        element.removeAttribute('aria-current');
        element.setAttribute('role', 'cell');
      }
    }
  };

  const setCustomAriaLabelForSelectedDate = () => {
    const node = reference?.current?.calendar?.componentNode;
    if (node) {
      Array.from(node.querySelectorAll('.react-datepicker__day')).forEach((element) => {
        const ariaLabel = element.getAttribute('aria-label');
        // Translate all existing ARIA labels when the calendar is opened for the first time.
        if (ariaLabel?.startsWith('Choose')) {
          const values = ariaLabel.split('Choose');
          if (values && values.length > 1) {
            element.removeAttribute('aria-label');
            element.setAttribute('aria-label', t('Choose {{ dateSelected }}', { dateSelected: values[1].trim() }));
          }
        }
        // Update roles to match the correct structure
        element.setAttribute('role', 'option');
        // This will reset any previous instances of custom ARIA labels that were set on the dates before.
        if (ariaLabel?.includes(t('press enter to confirm'))) {
          element.removeAttribute('aria-label');
          element.setAttribute('aria-label', ariaLabel.split(`, ${t('press enter to confirm')}`)[0]);
        }
      });
      // Get the selected date and set custom ARIA label for the selected date.
      const daySelected = Array.from(node.getElementsByClassName('react-datepicker__day--selected'));
      const keyboardSelected = Array.from(node.getElementsByClassName('react-datepicker__day--keyboard-selected'));
      const elements = keyboardSelected.length ? keyboardSelected : daySelected;
      elements.forEach((element) => {
        const ariaLabel = element.getAttribute('aria-label');
        const customAriaLabel = `${ariaLabel}, ${t(
          'press enter to confirm or press tab to confirm, cancel, or change to a different month. To select a different day, use the arrow keys.'
        )}`;
        element.removeAttribute('aria-label');
        element.setAttribute('aria-label', customAriaLabel);
      });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setAriaLabel();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, props]);

  return (
    <>
      <div
        className={classNames({
          [styles.label]: true,
          [styles.dark]: dark,
          [styles.error]: localError
        })}
      >
        {label && (
          <label htmlFor={id}>
            {label ? label : ''} {label ? (mandatory ? '*' : '') : ''}
          </label>
        )}
        <span className="visually-hidden" id={`${id}-format`}>
          {t(
            'Type the date in date format 2 digit month slash 2 digit day slash 4 digit year or press tab to use the date picker pop up calendar'
          )}
        </span>
        <div className={styles.container}>
          <DatePicker_
            ariaDescribedBy={localError ? `${name}-error-message` : `${id}-format`}
            ariaInvalid={localError ? 'true' : undefined}
            ariaLabelledBy={id}
            ariaRequired={mandatory === true ? 'true' : undefined}
            autoComplete="off"
            id={id}
            calendarStartDay={0}
            className={classNames({
              [styles['date-picker']]: true,
              [styles.dark]: dark,
              [styles.error]: localError,
              [styles['trip-search']]: isTripSearch
            })}
            formatWeekDay={formatWeekDay}
            onBlur={() => {
              if (props.value) {
                setLocalError(undefined);
              }
            }}
            onClickOutside={(event: React.MouseEvent<HTMLDivElement | MouseEvent>) => {
              if ((event.target as HTMLDivElement).getAttribute('data-type') !== `icon-${id}`) {
                setOpen(false);
              }
            }}
            onKeyDown={handleKeyDown}
            onSelect={(date: Date, e: any) => {
              const node = reference?.current?.calendar?.componentNode;
              if (node) {
                setValue(date);
                setTimeout(() => {
                  Array.from(node.querySelectorAll('.react-datepicker__day')).forEach((element) => {
                    const ariaLabel = element.getAttribute('aria-label');
                    element.classList.remove('react-datepicker__day--selected');
                  });
                  const elements = Array.from(node.getElementsByClassName('react-datepicker__day--keyboard-selected'));
                  if (elements.length === 0) {
                    e.target.classList.add('react-datepicker__day--selected');
                  } else {
                    elements.forEach((element) => {
                      element.classList.remove('react-datepicker__day--keyboard-selected');
                      element.classList.add('react-datepicker__day--selected');
                    });
                  }
                  onConfirm(date);
                  setOpen(false);
                  ref?.current?.focus();
                });
              }
            }}
            open={open}
            onChangeRaw={(e: any) => {
              const targetValue = e.target?.value || undefined;
              if (e.target?.value || e.target?.value?.length == 0) {
                // @ts-ignore
                props.onChange(targetValue);
              }
            }}
            placeholderText={placeholder}
            // @ts-ignore
            ref={reference}
            renderCustomHeader={renderCustomHeader}
            selected={value}
            shouldCloseOnSelect={false}
            popperClassName={props.isBookingWidget ? 'widget-popper-class' : ''}
            {...rest}
          >
            <div className="react-datepicker__children-container">
              <div className={`buttons ${props.hideControls ? 'hide' : ''}`}>
                <button
                  className="cancel"
                  onClick={() => {
                    setOpen(false);
                    if (name === 'schedule-date-picker' && props.onCancel) {
                      props.onCancel();
                    }
                    ref?.current?.focus();
                  }}
                  type="button"
                >
                  <span>{t('Cancel')}</span>
                </button>
                <button
                  className="confirm"
                  onClick={() => {
                    const elements = Array.from(
                      document.getElementsByClassName('react-datepicker__day--keyboard-selected')
                    );
                    if (elements.length) {
                      (elements[0] as HTMLElement).click();
                    }
                    ref?.current?.focus();
                    setOpen(false);
                  }}
                  type="button"
                >
                  <span>{t('Confirm')}</span>
                </button>
              </div>
              <div className="footer">{t('Cursor keys can navigate dates.')}</div>
            </div>
          </DatePicker_>
          <button
            ref={ref}
            className={classNames({
              [styles.icon]: true,
              ['gtm-open_date_picker']: true,
              [styles.dark]: dark,
              [styles.hide]: props.hideControls
            })}
            data-type={`icon-${id}`}
            onClick={() => {
              setOpen(!open);
              setTimeout(() => {
                modifyScreenReaderAnnouncement();
                setCustomAriaLabelForSelectedDate();
                const months_ = document.getElementsByClassName('react-datepicker__month');
                setAriaLabel();
                if (!value) {
                  const [element] = Array.from(
                    document.getElementsByClassName('react-datepicker__day--keyboard-selected')
                  );
                  if (element) {
                    element.classList.add('react-datepicker__day--selected');
                    (element as HTMLElement).focus();
                  } else {
                    // If we don't know what to focus on, find the first non-disabled day
                    const elements = Array.from(document.querySelectorAll('.react-datepicker__day'));
                    for (const element of elements) {
                      if (!element.classList.contains('react-datepicker__day--disabled')) {
                        // @ts-ignore
                        const currentInput = reference?.current?.input;
                        // The following addition enables the use of arrow keys for selecting the return date on the calendar.
                        if (currentInput) {
                          (currentInput as HTMLInputElement).focus();
                        }
                        (element as HTMLDivElement).focus();
                        break;
                      }
                    }
                  }
                } else {
                  const [element] = Array.from(document.getElementsByClassName('react-datepicker__day--selected'));
                  if (element) {
                    (element as HTMLElement).focus();
                  }
                }
              });
            }}
            type="button"
          >
            <span className="visually-hidden">{t('Open the calendar.')}</span>
          </button>
        </div>
        {localError && (
          <div
            className={classNames({
              [styles['error-message']]: true,
              [styles.dark]: dark,
              [`${name}-error-message`]: localError.normal
            })}
          >
            <span className="visually-hidden">{localError.screenReader}</span>
            {localError.normal}
            <span className="visually-hidden">{t('When you have corrected the input, press enter.')}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default DatePicker;
